import { DocArchivedError, DocLimitError, type StoredDocEnvelope } from '@/doc-sync/doc-store';

/** Saves work the user is bringing into their account (an import, a claimed
 *  `/try` draft) without ever refusing it for want of room:
 *    - 'active'   saved as an ordinary active document;
 *    - 'archived' the account has no free active slot, so the document is stored
 *                 archived instead (DocLimitError), or its id already exists
 *                 archived on the server (DocArchivedError).
 *
 *  Only DocLimitError retries with `saveArchived`. A create for an id that
 *  already exists archived answers 423, and retrying that with `archive: true`
 *  would 423 again; the work is already in the account, so it reports 'archived'
 *  and does not throw. Anything else — offline, too large, Pro required — is not
 *  this helper's to swallow and rethrows. The device copy is written before the
 *  server is asked, so it is there on every outcome.
 *
 *  A store without `saveArchived` (the local-only store, which has no limit to
 *  hit) rethrows a limit refusal rather than guessing. */
export async function saveBringingIn<T extends StoredDocEnvelope>(
    store: { save(doc: T): Promise<void>; saveArchived?(doc: T): Promise<void> },
    doc: T,
): Promise<'active' | 'archived'> {
    try {
        await store.save(doc);
        return 'active';
    } catch (err) {
        if (err instanceof DocArchivedError) return 'archived';
        if (!(err instanceof DocLimitError) || store.saveArchived == null) throw err;
        await store.saveArchived(doc);
        return 'archived';
    }
}
